import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Package, Calendar, Save, AlertCircle, Plane } from "lucide-react";
import theme from "@utils/theme";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import { Store as StoreApi } from "@apis/index";
import { toast } from "react-toastify";
import StoreAvailability from "@components/store-availability";
import DeliveryTime from "@components/fields/deliverytime";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import {
  Container,
  ErrorMessage,
  FormContainer,
  Input,
  InputGrid,
  InputGroup,
  Label,
  Section,
  SectionDescription,
  SectionHeader,
  SectionIcon,
  SectionTitle,
  SubmitButton,
  Switch,
  VacationDescription,
  VacationHeader,
  VacationIcon,
  VacationLeft,
  VacationMode,
  VacationTitle,
  DateInputWrapper,
  DeliveryTimeWrapper,
} from "./style";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";

const colors = {
  primary: theme.orange.orange500,
  primaryLight: theme.orange.orange100,
  primaryDark: theme.orange.orange600,
  success: "#10b981",
  error: "#ef4444",
  warning: "#f59e0b",
  gray50: "#f8fafc",
  gray100: "#f1f5f9",
  gray200: "#e2e8f0",
  gray300: "#cbd5e1",
  gray400: "#94a3b8",
  gray500: "#64748b",
  gray600: "#475569",
  gray700: "#334155",
  gray800: "#1e293b",
  white: "#ffffff",
};

// Main Component
const Settings = ({ state }: { state: any }) => {
  const { data: singleStore, loading } = useSelector(
    (state: RootState) => state.singleStore,
  );
  console.log({ singleStore });
  const fetchSingleStore = () => {
    StoreApi.find({ state: "singleStore", id: state?.id });
  };
  useEffect(() => {
    fetchSingleStore();
  }, []);
  const { settings } = useGeneralSettings();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const daysOfWeek = [
    { key: "sunday", label: "Sun" },
    { key: "monday", label: "Mon" },
    { key: "tuesday", label: "Tue" },
    { key: "wednesday", label: "Wed" },
    { key: "thursday", label: "Thu" },
    { key: "friday", label: "Fri" },
    { key: "saturday", label: "Sat" },
  ];

  const generateDefaultAvailability = () => {
    return daysOfWeek.map((day) => ({
      day: day.key,
      isClosed: false,
      is24Hours: true,
      timeSlots: [],
    }));
  };

  const [initialValues, setInitialValues] = useState({
    // Order Settings
    minimumOrderAmount: state?.minimumOrderAmount || "",
    processingTime: state?.processingTime || "",
    approximateDeliveryTime: state?.estimatedDeliveryTime || "",

    // Vacation Mode
    vacationMode: state?.vacationMode || false,
    vacationModeFrom:
      state?.vacationModeFrom && state.vacationModeFrom !== "null"
        ? new Date(state.vacationModeFrom)
        : null,
    vacationModeTo:
      state?.vacationModeTo && state.vacationModeTo !== "null"
        ? new Date(state.vacationModeTo)
        : null,

    availability: singleStore?.availabilities || generateDefaultAvailability(),
  });
  useEffect(() => {
    if (singleStore && singleStore.id) {
      setInitialValues({
        // Order Settings
        minimumOrderAmount: singleStore?.minimumOrderAmount || "",
        processingTime: singleStore?.processingTime || "",
        approximateDeliveryTime: singleStore?.estimatedDeliveryTime || "",

        // Vacation Mode
        vacationMode: singleStore?.vacationMode || false,
        vacationModeFrom:
          singleStore?.vacationModeStartDate &&
          singleStore.vacationModeStartDate !== "null"
            ? new Date(singleStore.vacationModeStartDate)
            : null,
        vacationModeTo:
          singleStore?.vacationModeEndDate &&
          singleStore.vacationModeEndDate !== "null"
            ? new Date(singleStore.vacationModeEndDate)
            : null,

        availability:
          Array.isArray(singleStore?.availabilities) &&
          singleStore.availabilities.length > 0
            ? singleStore?.availabilities?.map((a: any) => ({
                day: a.day,
                isClosed: a.isClosed,
                is24Hours: a.is24Hour,
                timeSlots: Array.isArray(a.slots)
                  ? a?.slots?.map((slot: any) => ({
                      startTime: slot.startTime,
                      endTime: slot.endTime,
                    }))
                  : [],
              }))
            : generateDefaultAvailability(),
      });
    }
  }, [singleStore]);

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    const formData: any = new FormData();
    setIsSubmitting(true);
    try {
      // Append all the basic fields to formData
      formData.append("minimumOrderAmount", values.minimumOrderAmount || "");
      formData.append("processingTime", values.processingTime || "");
      formData.append(
        "estimatedDeliveryTime",
        values.approximateDeliveryTime || "",
      );
      formData.append("vacationMode", values.vacationMode ? "true" : "false");

      if (values.vacationMode) {
        if (values.vacationModeFrom) {
          formData.append(
            "vacationModeFrom",
            new Date(values.vacationModeFrom).toISOString(),
          );
        }
        if (values.vacationModeTo) {
          formData.append(
            "vacationModeTo",
            new Date(values.vacationModeTo).toISOString(),
          );
        }
      }

      // Append commission and plan if they exist in state
      if (state?.commission !== undefined) {
        formData.append("commission", state.commission);
      }
      if (state?.plan) {
        formData.append("plan", state.plan);
      }

      // Append availabilities
      if (
        Array.isArray(values.availability) &&
        values.availability.length > 0
      ) {
        const availabilitiesPayload = values.availability?.map((a) => ({
          day: a.day,
          isClosed: !!a.isClosed,
          is24Hour: !!a.is24Hours,
          slots: Array.isArray(a.timeSlots)
            ? a?.timeSlots?.map((slot) => ({
                startTime: slot.startTime,
                endTime: slot.endTime,
              }))
            : [],
        }));

        formData.append(
          "availabilities",
          JSON.stringify(availabilitiesPayload),
        );
      }
      if (state) {
        await StoreApi.update({
          state: "stores",
          id: state?.id,
          data: formData,
        });

        // Refetch to update Redux state
        fetchSingleStore();
      }

      toast.success("Settings saved successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Error saving settings. Please try again.");
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  return (
    <Container>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
        }) => (
          <FormContainer>
            {/* Order Settings Section */}
            <Section>
              <SectionHeader>
                <SectionIcon>
                  <Package size={20} />
                </SectionIcon>
                <div>
                  <SectionTitle>Order Settings</SectionTitle>
                  <SectionDescription>
                    Configure minimum order requirements and delivery times
                  </SectionDescription>
                </div>
              </SectionHeader>

              <InputGrid>
                <InputGroup>
                  <Label>
                    Minimum Order Amount ({settings.currencySymbol})
                  </Label>
                  <Input
                    type="number"
                    name="minimumOrderAmount"
                    value={values.minimumOrderAmount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                  {touched.minimumOrderAmount && errors.minimumOrderAmount && (
                    <ErrorMessage>
                      <AlertCircle size={12} />
                      {errors.minimumOrderAmount}
                    </ErrorMessage>
                  )}
                </InputGroup>

                <InputGroup>
                  <Label>Processing Time (min)</Label>
                  <Input
                    type="number"
                    name="processingTime"
                    value={values.processingTime}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="30"
                    min="0"
                  />
                  {touched.processingTime && errors.processingTime && (
                    <ErrorMessage>
                      <AlertCircle size={12} />
                      {errors.processingTime}
                    </ErrorMessage>
                  )}
                </InputGroup>

                <InputGroup>
                  <Label>Delivery Time</Label>
                  <DeliveryTimeWrapper>
                    <DeliveryTime name="approximateDeliveryTime" />
                  </DeliveryTimeWrapper>
                </InputGroup>
              </InputGrid>
            </Section>

            {/* Vacation Mode Section */}
            <Section>
              <SectionHeader>
                <SectionIcon>
                  <Plane size={20} />
                </SectionIcon>
                <div>
                  <SectionTitle>Store Status</SectionTitle>
                  <SectionDescription>
                    Manage your store's availability and vacation mode
                  </SectionDescription>
                </div>
              </SectionHeader>

              <VacationMode>
                <VacationHeader>
                  <VacationLeft>
                    <VacationIcon>
                      <Plane size={16} />
                    </VacationIcon>
                    <div>
                      <VacationTitle>Vacation Mode</VacationTitle>
                      <VacationDescription>
                        {values.vacationMode
                          ? "Store is currently closed. Customers cannot place orders until vacation mode is turned off."
                          : "Store is currently open. Turn on vacation mode to temporarily close your store."}
                      </VacationDescription>
                    </div>
                  </VacationLeft>

                  <Switch checked={values.vacationMode}>
                    <input
                      type="checkbox"
                      name="vacationMode"
                      checked={values.vacationMode}
                      onChange={(e) =>
                        setFieldValue("vacationMode", e.target.checked)
                      }
                    />
                    <span className="slider" />
                  </Switch>
                </VacationHeader>

                {values.vacationMode && (
                  <InputGrid style={{ marginTop: "16px", gap: "46px" }}>
                    <InputGroup>
                      <Label>From Date</Label>
                      <DateInputWrapper>
                        <DatePicker
                          selected={values.vacationModeFrom}
                          onChange={(date: Date | null) =>
                            setFieldValue("vacationModeFrom", date)
                          }
                          placeholderText="Select start date"
                          dateFormat="MMM d, yyyy"
                          minDate={new Date()}
                        />
                      </DateInputWrapper>
                    </InputGroup>

                    <InputGroup>
                      <Label>To Date</Label>
                      <DateInputWrapper>
                        <DatePicker
                          selected={values.vacationModeTo}
                          onChange={(date: Date | null) =>
                            setFieldValue("vacationModeTo", date)
                          }
                          placeholderText="Select end date"
                          dateFormat="MMM d, yyyy"
                          minDate={values.vacationModeFrom || new Date()}
                          disabled={!values.vacationModeFrom}
                        />
                      </DateInputWrapper>
                    </InputGroup>
                  </InputGrid>
                )}
              </VacationMode>
            </Section>

            <StoreAvailability
              value={values.availability || []}
              onChange={(schedules) => setFieldValue("availability", schedules)}
            />

            <SubmitButton type="submit" disabled={isSubmitting}>
              <Save size={16} />
              {isSubmitting ? "Saving..." : "Save"}
            </SubmitButton>
          </FormContainer>
        )}
      </Formik>
    </Container>
  );
};

export default Settings;
